import React from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';

export default function BatchEnrollmentCard({
  archived,
  adding,
  studentsLoading,
  addableOptions,
  toAdd,
  onToAddChange,
  onAdd,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.75 },
        mb: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Enrollment
        </Typography>
        {toAdd.length > 0 ? (
          <Chip size="small" label={`${toAdd.length} selected`} color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.25, maxWidth: 640 }}>
        Search your linked students and add them to this batch. Only students with an accepted teacher connection appear here.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
        <Autocomplete
          multiple
          loading={studentsLoading}
          options={addableOptions}
          getOptionLabel={(o) => {
            if (!o) return '';
            const name = o.label || (o.student_name || '').trim();
            const em = o.email || o.student_email || '';
            if (name && em) return `${name} (${em})`;
            return em || name || `Student #${o.id}`;
          }}
          isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
          filterSelectedOptions
          value={toAdd}
          onChange={(_, v) => onToAddChange(v)}
          disabled={archived || adding || studentsLoading}
          sx={{ flex: 1, minWidth: 0, width: { xs: '100%', md: 'auto' } }}
          noOptionsText={
            studentsLoading
              ? 'Loading students…'
              : addableOptions.length === 0
                ? 'Everyone in your roster is already in this batch, or you have no accepted students yet.'
                : 'No matches'
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Find students"
              placeholder="Type a name or email"
              helperText={null}
              InputProps={{
                ...params.InputProps,
                sx: { borderRadius: 2, bgcolor: 'background.paper' },
              }}
            />
          )}
        />
        <Button
          variant="contained"
          disabled={archived || adding || toAdd.length === 0 || studentsLoading}
          onClick={onAdd}
          startIcon={<PersonAddAltOutlinedIcon />}
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            py: 1.25,
            minWidth: { md: 200 },
            alignSelf: { xs: 'stretch', md: 'flex-start' },
            borderRadius: 2,
            boxShadow: (t) => (archived || toAdd.length === 0 ? 'none' : t.shadows[2]),
            '&:focus-visible': { outline: '2px solid', outlineOffset: 2, outlineColor: 'primary.main' },
          }}
        >
          {adding ? 'Adding…' : 'Add to batch'}
        </Button>
      </Stack>
    </Paper>
  );
}
